
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SpecialEvent, SpecialEventOption } from '../../types/specialEvent';
import { useGame } from '../../contexts/GameContext';
import { Check, Award, TrendingUp, LightbulbIcon, AlertCircle } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { motion, AnimatePresence } from "framer-motion";

interface SpecialEventModalProps {
  event: SpecialEvent;
  onClose: () => void;
}

const SpecialEventModal: React.FC<SpecialEventModalProps> = ({ event, onClose }) => {
  const { makeDecision, updateKpi, addSkill, selectedRole, skills: playerSkills, careerLevel } = useGame();
  const [selectedOption, setSelectedOption] = useState<SpecialEventOption | null>(null);
  const [showOutcome, setShowOutcome] = useState(false);
  
  const getEventIcon = () => {
    switch (event.type) {
      case 'market':
        return <TrendingUp className="mr-2 text-bitcoin-primary h-6 w-6" />;
      case 'technology':
        return <LightbulbIcon className="mr-2 text-bitcoin-primary h-6 w-6" />;
      case 'regulatory':
        return <AlertCircle className="mr-2 text-bitcoin-primary h-6 w-6" />;
      case 'opportunity':
        return <Award className="mr-2 text-bitcoin-primary h-6 w-6" />;
      default:
        return <Award className="mr-2 text-bitcoin-primary h-6 w-6" />;
    }
  };
  
  const getImpactColor = (value: number): string => {
    if (value > 0) return "text-bitcoin-accent";
    if (value < 0) return "text-destructive";
    return "text-muted-foreground";
  };
  
  const handleDecision = (option: SpecialEventOption) => {
    setSelectedOption(option);
    
    // Check for role-specific outcomes
    let effectsToApply = { ...option.kpiEffects };
    let experienceGain = option.experienceGain;
    let outcomeText = option.outcome;
    
    // Apply role-specific effects if applicable
    if (selectedRole && option.roleSpecificOutcome) {
      const roleOutcome = option.roleSpecificOutcome.find(
        outcome => outcome.roleId === selectedRole.id
      );
      
      if (roleOutcome) {
        effectsToApply = { ...effectsToApply, ...roleOutcome.kpiEffects };
        experienceGain = roleOutcome.experienceGain;
        outcomeText = roleOutcome.outcome;
        
        toast(`Role Advantage: ${selectedRole.title}!`, {
          description: "Your role gives you a special advantage in this situation.",
          duration: 4000,
        });
      }
    }
    
    // Apply skill-specific effects if applicable
    if (option.skillSpecificOutcome) {
      const playerSkillIds = playerSkills.map(skill => skill.id);
      
      for (const skillOutcome of option.skillSpecificOutcome) {
        if (playerSkillIds.includes(skillOutcome.skillId)) {
          const relevantSkill = playerSkills.find(s => s.id === skillOutcome.skillId);
          
          effectsToApply = { ...effectsToApply, ...skillOutcome.kpiEffects };
          experienceGain = Math.max(experienceGain, skillOutcome.experienceGain);
          outcomeText = skillOutcome.outcome;
          
          toast(`Skill Advantage: ${relevantSkill?.name}!`, {
            description: "Your skills give you a special advantage in this situation.",
            duration: 4000,
          });
          
          break; // Apply only the first matching skill outcome for balance
        }
      }
    }
    
    // Apply KPI effects with animation
    Object.entries(effectsToApply).forEach(([kpi, value]) => {
      updateKpi(kpi, value);
      
      if (Math.abs(value) >= 2 || (kpi === 'energyCost' && Math.abs(value) >= 0.001)) {
        const direction = value > 0 ? 'increased' : 'decreased';
        const absValue = Math.abs(value);
        
        toast(`${kpi} ${direction}!`, {
          description: `Your ${kpi} has ${direction} by ${absValue}${kpi === 'energyCost' ? '/kWh' : '%'}`,
          duration: 3000,
        });
      }
    });
    
    // Show experience gained with animation
    toast("Experience Gained!", {
      description: `You've gained ${experienceGain} experience points.`,
      icon: <Check className="h-4 w-4" />,
      duration: 3000,
    });
    
    makeDecision(option.text, outcomeText);
    
    if (option.skillUnlock) {
      addSkill(option.skillUnlock);
      toast("New Skill Unlocked!", {
        description: `You've gained the ${option.skillUnlock.name} skill!`,
        icon: <Award className="h-4 w-4" />,
        duration: 4000,
      });
    }
    
    // Show outcome with animation
    setTimeout(() => {
      setShowOutcome(true);
    }, 500);
    
    // Don't close immediately after selection to show the outcome
    setTimeout(() => {
      onClose();
    }, 5000);
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] p-0 max-h-[85vh] flex flex-col overflow-hidden animate-scale-in">
        <div className="bg-bitcoin-dark text-white p-5 border-b border-bitcoin-primary/20 flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center">
              {getEventIcon()} {event.title}
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-lg mt-2">
              Special Industry Event
            </DialogDescription>
          </DialogHeader>
        </div>
        
        <div className="p-6 overflow-y-auto flex-grow">
          <p className="mb-6 text-lg">{event.description}</p>
          
          <div className="space-y-4">
            <AnimatePresence>
              {event.options.map((option, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: index * 0.15 }}
                  className={`border rounded-md p-5 hover:border-bitcoin-primary cursor-pointer transition-all ${
                    selectedOption === option 
                      ? 'border-bitcoin-primary bg-bitcoin-primary/5 shadow-lg' 
                      : 'hover:shadow-md hover:translate-y-[-2px]'
                  }`}
                  onClick={() => !selectedOption && handleDecision(option)}
                >
                  <h3 className="font-medium text-lg mb-3">{option.text}</h3>
                  {selectedOption ? (
                    <div className="grid grid-cols-2 gap-3 text-sm text-muted-foreground mb-3 animate-fade-in">
                      {Object.entries(option.kpiEffects).map(([kpi, value]) => (
                        <div key={kpi} className="flex items-center">
                          <span className={getImpactColor(value)}>
                            {value > 0 ? "+" : ""}{value}
                          </span>
                          <span className="ml-2">{kpi}</span>
                        </div>
                      ))}
                      <div className="flex items-center text-bitcoin-accent">
                        +{option.experienceGain} XP
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">Select to see impact</div>
                  )}
                  {selectedOption === option && option.skillUnlock && (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-sm bg-bitcoin-primary/10 text-bitcoin-primary px-3 py-1.5 rounded inline-block mt-2"
                    >
                      <div className="flex items-center">
                        <Award className="h-4 w-4 mr-1.5" /> 
                        Unlocks: {option.skillUnlock.name}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          <AnimatePresence>
            {showOutcome && selectedOption && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-6 p-5 border border-web3-green/20 bg-web3-green/10 rounded-md"
              >
                <h3 className="font-medium mb-3 text-lg text-web3-green flex items-center">
                  <Check className="mr-2 h-5 w-5" /> Outcome
                </h3>
                <p className="text-white/90">{selectedOption.outcome}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpecialEventModal;
